import { useEffect, useRef, useState } from 'react';

import { PermissionStatus } from 'expo-modules-core';
import { IosAuthorizationStatus } from 'expo-notifications';
import { Platform } from 'react-native';

import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { useAttPermission } from 'state/app-tracking-transparency-permissions/common';
import { useOpenedByBranch } from 'state/branch/hooks/use-deeplinks';
import { useGeolocation } from 'state/geolocation';
import { OsPrompts } from 'state/location/routes';
import { addOrUpdateScreenState } from 'state/onboarding/utils';
import { usePushPermissions } from 'state/push-permissions';
import { Deferred } from 'utils/Deferred';
import { isCA, isIOS, isWeb } from 'utils/environment';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';

import { Channel, OnboardingScreen, OnboardingStorage, Onboardings, TriggerRule } from '../types';

import { useOnboardingScreens } from './use-onboarding-screens';

export const useOnboardingContent = () => {
  const onboardingState: OnboardingStorage | null = syncStorage.getObject(
    SyncStorageKey.ONBOARDING_STATE
  );
  const channel = isWeb ? Channel.WEB : isIOS() ? Channel.IOS : Channel.ANDROID;
  const [triggerRule, setTriggerRule] = useState<string>(TriggerRule.ON_FIRST_APP_LOAD_NEW_USER);
  const [onboardings, setOnboardings] = useState<Onboardings>();
  const [onboardingContentData, setOnboardingContentData] = useState<any>({
    firstAppLoadOnboarding: {
      id: '',
      featureOnboardingChannel: '',
      featureOnboardingTriggerRule: '',
      featureOnboardingScreens: [],
    },
    triggerRule,
  });

  const prevOnboardingScreensLoadingRef = useRef(true);

  const {
    isPermissionKnown: isLocationPermissionKnown,
    isLoadingPermission: isLoadingLocationPermission,
  } = useGeolocation();
  const [settings] = usePushPermissions();

  const isPushPermissionKnown =
    !!settings &&
    settings?.status !== PermissionStatus.UNDETERMINED &&
    settings?.ios?.status !== IosAuthorizationStatus.NOT_DETERMINED;

  const isLoadingPushPermission = !settings;
  const { featureOnboardings, loading: isOnboardingScreensLoading } = useOnboardingScreens();
  const { isAttPermissionKnown, isLoadingAttPermission } = useAttPermission();
  const openedByBranch = useOpenedByBranch();

  useEffect(() => {
    if (!onboardings) {
      setOnboardings(featureOnboardings);
    }
  }, [featureOnboardings, onboardings]);

  /**
   * Fire ONBOARDING_DATA exactly once when onboarding screens have finished loading.
   * Persisted in storage so we do not fire again on app reopen or on provider remount.
   */
  useEffect(() => {
    const loadingFinished = prevOnboardingScreensLoadingRef.current && !isOnboardingScreensLoading;
    prevOnboardingScreensLoadingRef.current = isOnboardingScreensLoading;

    const alreadySentPersisted = syncStorage.getBoolean(SyncStorageKey.ONBOARDING_DATA_SENT);
    if (!loadingFinished || alreadySentPersisted) {
      return;
    }

    syncStorage.setBoolean(SyncStorageKey.ONBOARDING_DATA_SENT, true);

    logRBIEvent({
      name: CustomEventNames.ONBOARDING_DATA,
      type: EventTypes.Other,
      attributes: {
        data: JSON.stringify({
          screensReceived:
            featureOnboardings?.[0]?.featureOnboardingScreens?.map(screen => screen.id) ?? [],
          savedState: onboardingState?.onboardingDocs ?? [],
        }),
      },
    });
  }, [isOnboardingScreensLoading, featureOnboardings, onboardingState]);

  const firstAppLoadOnboarding = onboardings?.find(
    onboarding =>
      onboarding.featureOnboardingChannel === channel &&
      onboarding.featureOnboardingTriggerRule === triggerRule
  );

  const everyScreenSavedIsCompleted = onboardingState?.onboardingDocs
    .filter(doc => doc.id === triggerRule)
    .every(doc => doc.screens.every(screen => screen.completed));

  const onboardingDocsScreensIds = new Set(
    onboardingState?.onboardingDocs
      ?.find(doc => doc.id === triggerRule)
      ?.screens.map(screen => screen.id)
  );

  const everySanityScreenIsCompleted = firstAppLoadOnboarding?.featureOnboardingScreens
    ?.map(docScreen => docScreen.id)
    ?.every(id => id && onboardingDocsScreensIds.has(id));

  useEffect(() => {
    if (
      isLoadingPushPermission ||
      isLoadingLocationPermission ||
      isLoadingAttPermission ||
      isOnboardingScreensLoading
    ) {
      return;
    }
    // filter screens that asks for permission that we already have
    if (firstAppLoadOnboarding && firstAppLoadOnboarding?.featureOnboardingScreens) {
      firstAppLoadOnboarding.featureOnboardingScreens = firstAppLoadOnboarding.featureOnboardingScreens.filter(
        screen => {
          switch (screen.buttonUrl) {
            case `/OS/${OsPrompts.push}`:
              if (isPushPermissionKnown && settings?.granted) {
                addOrUpdateScreenState(triggerRule || '', screen, screen.buttonUrl);
              }
              return (!isPushPermissionKnown || settings?.canAskAgain) && !settings?.granted;
            case `/OS/${OsPrompts.location}`:
              if (isLocationPermissionKnown) {
                addOrUpdateScreenState(triggerRule || '', screen, screen.buttonUrl);
              }
              return !isLocationPermissionKnown;
            case `/OS/${OsPrompts.att}`:
              if (Platform.OS !== 'ios') {
                addOrUpdateScreenState(triggerRule || '', screen, screen.buttonUrl);
                return false;
              }
              if (isAttPermissionKnown) {
                addOrUpdateScreenState(triggerRule || '', screen, screen.buttonUrl);
              }
              return !isAttPermissionKnown;
            default:
              return true;
          }
        }
      );
    }

    // remove screen completed
    if (
      onboardingState &&
      firstAppLoadOnboarding &&
      firstAppLoadOnboarding?.featureOnboardingScreens
    ) {
      firstAppLoadOnboarding.featureOnboardingScreens = firstAppLoadOnboarding.featureOnboardingScreens.filter(
        screen => {
          const completed = onboardingState?.onboardingDocs
            ?.find(doc => doc?.id === firstAppLoadOnboarding?.featureOnboardingTriggerRule)
            ?.screens.some((value: OnboardingScreen) => {
              return value.id === screen.id && value.completed;
            });
          return !completed;
        }
      );
    }
    const emptyFirstAppLoad = {
      firstAppLoadOnboarding: {
        id: '',
        featureOnboardingChannel: '',
        featureOnboardingTriggerRule: '',
        featureOnboardingScreens: [],
      },
      triggerRule,
    };

    if (openedByBranch) {
      setOnboardingContentData(emptyFirstAppLoad);
      return;
    }
    if (
      !firstAppLoadOnboarding &&
      (everySanityScreenIsCompleted ||
        isLoadingPushPermission ||
        isLoadingLocationPermission ||
        isLoadingAttPermission)
    ) {
      setOnboardingContentData(emptyFirstAppLoad);
    } else {
      setOnboardingContentData({
        firstAppLoadOnboarding,
        triggerRule,
      });
    }
    // we need to execute this hook just when triggerRule or firstAppLoadOnboarding change
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [triggerRule, firstAppLoadOnboarding, openedByBranch]);

  useEffect(() => {
    if (
      isLoadingPushPermission ||
      isLoadingLocationPermission ||
      isLoadingAttPermission ||
      isOnboardingScreensLoading
    ) {
      return;
    }

    if (
      everySanityScreenIsCompleted &&
      everyScreenSavedIsCompleted &&
      triggerRule === TriggerRule.ON_FIRST_APP_LOAD_NEW_USER
    ) {
      setTriggerRule(TriggerRule.ON_FIRST_APP_LOAD_EXISTING_USER);
    }

    if (everySanityScreenIsCompleted) {
      onboardingIsLikelyCompleteDeferred.resolve();
    }

    // We do not want the triggerRule to change once defined or while permissions are loading
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    triggerRule,
    isLoadingLocationPermission,
    isLoadingPushPermission,
    isLoadingAttPermission,
    firstAppLoadOnboarding,
    everySanityScreenIsCompleted,
    everyScreenSavedIsCompleted,
  ]);

  return onboardingContentData;
};

// this is sanity driven so this isn't 100% reliable but should be OK for most purposes (like home page animations etc.)
// we don't want to delay home page animations/audio for onboarding call that is likely done for best user experience
// TODO we need to rethink/rewrite onboarding to be simpler, not sanity driven, route driven https://rbictg.atlassian.net/browse/BKPE-24180
export const onboardingIsLikelyCompleteDeferred = new Deferred();
const hasOnboardingState = syncStorage.getObject(SyncStorageKey.ONBOARDING_STATE);
if (hasOnboardingState) {
  onboardingIsLikelyCompleteDeferred.resolve();
}
if (isWeb || isCA) {
  // web and CA has no onboarding
  onboardingIsLikelyCompleteDeferred.resolve();
}
